import React from 'react';
import HelpBadge from './components/HelpBadge';

type Source = {
  id: string;
  kind: string;
  kindLabel?: string;
  delivery?: string;
  kindSummary?: string;
  label: string;
  href?: string;
  detail?: string;
  enabled: boolean;
  discovered?: boolean;
};

type Overview = {
  company_id: number;
  company_name: string;
  sources?: Source[];
  notes?: string;
  note?: string;
  owner?: string;
  updated_at?: string;
  scan?: { pages?: string[]; errors?: Array<{ url: string; error: string }>; found?: number };
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  companyId: number | null;
  language?: 'en' | 'es';
};

const RazzmatazzBookingPanel: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  companyId,
  language = 'en',
}) => {
  const spanish = language === 'es';
  const [data, setData] = React.useState<Overview | null>(null);
  const [checked, setChecked] = React.useState<Set<string>>(new Set());
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const headers = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!companyId) {
      setData(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/booking-intake?company_id=${companyId}`,
        { headers },
      );
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `Booking intake returned ${response.status}`);
      }
      setData(payload);
      setChecked(new Set((payload.sources || []).filter((row) => row.enabled).map((row) => row.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [companyId, gatewayBaseUrl, headers]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/booking-intake`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: companyId,
          sourceIds: [...checked],
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `Save failed (${response.status})`);
      }
      setData(payload);
      setChecked(new Set((payload.sources || []).filter((row) => row.enabled).map((row) => row.id)));
      setNote(spanish ? 'Calendarios de marketing guardados.' : 'Marketing calendars saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const discover = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/booking-intake/discover`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ company_id: companyId }),
      });
      const payload = (await response.json().catch(() => ({}))) as Overview & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `Scan failed (${response.status})`);
      }
      setData(payload);
      setChecked(new Set((payload.sources || []).filter((row) => row.enabled).map((row) => row.id)));
      const found = payload.scan?.found ?? (payload.sources || []).length;
      setNote(
        spanish
          ? `Escaneo listo · ${found} calendario(s). Marca los de marketing y guarda.`
          : `Scan complete · ${found} calendar(s). Check the marketing ones and save.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!companyId) {
    return (
      <div className="razz-notify">
        <p className="razz-muted">
          {spanish
            ? 'Elige un proyecto para configurar calendarios de preventa.'
            : 'Pick a project to configure presales booking calendars.'}
        </p>
      </div>
    );
  }

  const sources = data?.sources || [];

  return (
    <div className="razz-notify razz-booking">
      <div className="razz-traffic-toolbar">
        <div>
          <h3>
            {spanish ? 'Reservas / calendarios' : 'Bookings / calendars'}
            <HelpBadge label={spanish ? 'Quién es dueño' : 'Who owns this'}>
              <p>
                {spanish
                  ? 'Preventa: marketing crea leads desde estas reservas y trabaja con ventas (Pursuit) para convertir. CompliDesk no es el dueño.'
                  : 'Presales: marketing owns these booking calendars, creates leads, and works with sales (Pursuit) to convert. CompliDesk does not own them.'}
              </p>
            </HelpBadge>
          </h3>
          <p className="razz-muted">
            {data?.note
              || (spanish
                ? 'Selecciona los calendarios del sitio que alimentan leads de marketing.'
                : 'Select site calendars that feed marketing leads.')}
          </p>
        </div>
        <div className="razz-traffic-controls">
          <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void load()}>
            {spanish ? 'Actualizar' : 'Refresh'}
          </button>
          <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void discover()}>
            {busy ? (spanish ? 'Escaneando…' : 'Scanning…') : (spanish ? 'Escanear sitio' : 'Scan site')}
          </button>
        </div>
      </div>

      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {note ? <p className="razz-notify-saved" role="status">{note}</p> : null}

      <section className="razz-booking-list" aria-label={spanish ? 'Calendarios' : 'Calendars'}>
        {sources.length === 0 ? (
          <p className="razz-muted">
            {spanish
              ? 'Aún no hay calendarios. Escanea el sitio o espera el catálogo del proyecto.'
              : 'No calendars yet. Scan the site or wait for the project catalog.'}
          </p>
        ) : (
          <ul className="razz-booking-sources">
            {sources.map((row) => (
              <li key={row.id}>
                <label className="razz-booking-row">
                  <input
                    type="checkbox"
                    checked={checked.has(row.id)}
                    disabled={busy}
                    onChange={() => {
                      setChecked((current) => {
                        const next = new Set(current);
                        if (next.has(row.id)) next.delete(row.id);
                        else next.add(row.id);
                        return next;
                      });
                    }}
                  />
                  <span>
                    <strong>{row.label}</strong>
                    <span className="razz-muted">
                      {row.detail || row.href || row.id}
                      {row.discovered ? (spanish ? ' · descubierto' : ' · discovered') : ''}
                    </span>
                    {row.href ? (
                      <a href={row.href} target="_blank" rel="noopener noreferrer">
                        {spanish ? 'Abrir' : 'Open'}
                      </a>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(data?.scan?.errors || []).length ? (
        <p className="razz-muted" role="status">
          {(data?.scan?.errors || []).map((item) => `${item.url}: ${item.error}`).join(' · ')}
        </p>
      ) : null}

      <div className="razz-notify-actions">
        <button
          type="button"
          className="dashboard-security-inline-btn active razz-notify-save"
          disabled={busy}
          onClick={() => void save()}
        >
          {busy ? (spanish ? 'Guardando…' : 'Saving…') : (spanish ? 'Guardar calendarios' : 'Save calendars')}
        </button>
        <p className="razz-muted">
          {spanish
            ? `${checked.size} seleccionado(s) · leads → marketing → ventas`
            : `${checked.size} selected · leads → marketing → sales`}
        </p>
      </div>
    </div>
  );
};

export default RazzmatazzBookingPanel;
